/**
 * Shared interface primitives.
 *
 * These carry the EJOT presentation rules that must hold everywhere: a status
 * is never communicated by colour alone, a blocked verification never reads
 * as a pass, and engineering symbols keep their notation.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { CalculationStatus, Severity, ValidationMessage } from '../gen/ejot/roofing/v1/types';
import { formatDisplayUnit, parseTechnicalSymbol } from './format';
import { SectionIcon, sectionIconForTitle } from './SectionIcon';

// ── Panels and sections ──────────────────────────────────────────────────────

export function Panel({ title, children, actions }: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const icon = sectionIconForTitle(title);
  return (
    <section className="panel">
      <header className="panel-header">
        <span className="heading-with-icon">
          {icon && <SectionIcon name={icon} className="section-heading-icon" />}
          <span>{title}</span>
        </span>
        {actions}
      </header>
      <div className="panel-scroll">{children}</div>
    </section>
  );
}

/** A collapsible EJOT-style section card. */
export function SectionCard({ title, children, defaultOpen = true }: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const icon = sectionIconForTitle(title);
  return (
    <details className="section-card" open={defaultOpen}>
      <summary className="section-card-header">
        <span className="heading-with-icon">
          {icon && <SectionIcon name={icon} className="section-heading-icon" />}
          <span>{title}</span>
        </span>
      </summary>
      <div className="section-card-body">{children}</div>
    </details>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

export interface TabDefinition {
  id: string;
  label: string;
  badge?: number;
  /** Errors are the stronger claim and are coloured as such. */
  badgeTone?: 'warning' | 'error';
  /**
   * What the count means, for anyone who cannot see the colour that would
   * otherwise carry it. A bare number on a tab says nothing on its own.
   */
  badgeDescription?: string;
}

export function TabBar({ tabs, active, onSelect, ariaLabel }: {
  tabs: readonly TabDefinition[];
  active: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
}) {
  return (
    <div className="tab-bar" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={tab.id === active}
          aria-controls={`panel-${tab.id}`}
          className={tab.id === active ? 'tab is-active' : 'tab'}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 && (
            <span
              className={tab.badgeTone === 'error' ? 'tab-badge is-error' : 'tab-badge'}
              title={tab.badgeDescription}
              aria-label={tab.badgeDescription}
            >
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Contextual information ───────────────────────────────────────────────────

export function InfoButton({ children, label = 'More information' }: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <details className="info-control">
      <summary className="info-button" aria-label={label} title={label}>i</summary>
      <div className="info-popover" role="note">{children}</div>
    </details>
  );
}

// ── Form primitives ──────────────────────────────────────────────────────────

/**
 * Text and numeric engineering inputs are transactional. Typing only edits a
 * local draft; the parent contract is mutated on blur or Enter. Escape cancels
 * the draft. This prevents transient values such as 1000 -> 100 -> 10 -> 1
 * from becoming calculation inputs while a user is still editing a field.
 */
export function Field({ id: explicitId, label, symbol, unit, value, derived = false, onChange, type = 'text', help }: {
  id?: string;
  label: string;
  symbol?: string;
  unit?: string;
  value: string;
  derived?: boolean;
  onChange?: (value: string) => void;
  type?: 'text' | 'number';
  help?: ReactNode;
}) {
  const id = explicitId ?? `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const [draftError, setDraftError] = useState<string>();
  const suppressNextBlur = useRef(false);
  const editable = !derived && Boolean(onChange);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
      setDraftError(undefined);
    }
  }, [editing, value]);

  const validateDraft = (candidate: string): string | undefined => {
    if (type !== 'number') return undefined;
    if (candidate.trim() === '') return 'Enter a number before leaving this field.';
    const parsed = Number(candidate);
    if (!Number.isFinite(parsed)) return 'Enter a valid finite number.';
    return undefined;
  };

  const commitDraft = (): boolean => {
    if (!editable || !onChange) return true;
    const error = validateDraft(draft);
    if (error) {
      setDraftError(error);
      setEditing(true);
      return false;
    }
    setDraftError(undefined);
    setEditing(false);
    if (draft !== value) onChange(draft);
    return true;
  };

  const cancelDraft = (input: HTMLInputElement) => {
    suppressNextBlur.current = true;
    setDraft(value);
    setDraftError(undefined);
    setEditing(false);
    input.blur();
  };

  return (
    <div className="field">
      <div className="field-heading">
        <label className="field-label" htmlFor={id}>
          <span>{label}{symbol && <> <Symbol symbol={symbol} /></>}</span>
          {unit && <span className="field-unit">{formatDisplayUnit(unit)}</span>}
        </label>
        {help && <InfoButton label={`Information about ${label}`}>{help}</InfoButton>}
      </div>
      <input
        id={id}
        className={`${derived ? 'field-input is-derived' : 'field-input'}${draftError ? ' is-invalid' : ''}`}
        type={type}
        value={draft}
        readOnly={!editable}
        aria-readonly={!editable}
        aria-invalid={draftError ? true : undefined}
        aria-describedby={draftError ? `${id}-draft-error` : undefined}
        onFocus={editable ? () => {
          if (!editing) setDraft(value);
          setEditing(true);
        } : undefined}
        onChange={editable ? (event) => {
          setDraft(event.target.value);
          if (draftError) setDraftError(undefined);
        } : undefined}
        onBlur={editable ? () => {
          if (suppressNextBlur.current) {
            suppressNextBlur.current = false;
            return;
          }
          commitDraft();
        } : undefined}
        onKeyDown={editable ? (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (commitDraft()) {
              suppressNextBlur.current = true;
              event.currentTarget.blur();
            }
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            cancelDraft(event.currentTarget);
          }
        } : undefined}
      />
      {draftError && (
        <p id={`${id}-draft-error`} className="field-note field-note-blocking" role="alert">
          {draftError}
        </p>
      )}
    </div>
  );
}

export function SelectField({ label, value, options, onChange, placeholder, help }: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange?: (value: string) => void;
  placeholder?: string;
  help?: ReactNode;
}) {
  const id = `select-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <div className="field-heading">
        <label className="field-label" htmlFor={id}>{label}</label>
        {help && <InfoButton label={`Information about ${label}`}>{help}</InfoButton>}
      </div>
      <select
        id={id}
        className="field-input"
        value={value}
        disabled={options.length === 0 || !onChange}
        onChange={onChange ? (event) => onChange(event.target.value) : undefined}
      >
        {options.length === 0 && <option value="">{placeholder ?? 'None available'}</option>}
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="field-row">{children}</div>;
}

export function CheckboxField({ label, checked, onChange }: {
  label: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <label className="checkbox-field">
      <input
        type="checkbox"
        checked={checked}
        disabled={!onChange}
        onChange={onChange ? (event) => onChange(event.target.checked) : undefined}
      />
      <span>{label}</span>
    </label>
  );
}

export function Symbol({ symbol }: { symbol: string }) {
  const { base, sub, sup, suffix } = parseTechnicalSymbol(symbol);
  return (
    <span className="technical-symbol">
      {base}{sub && <sub>{sub}</sub>}{sup && <sup>{sup}</sup>}{suffix}
    </span>
  );
}

// ── Status ───────────────────────────────────────────────────────────────────

const STATUS_PRESENTATION: Record<CalculationStatus, { label: string; className: string }> = {
  CALCULATION_STATUS_UNSPECIFIED: { label: 'Unknown', className: 'is-blocked' },
  CALCULATION_STATUS_PASS: { label: 'Pass', className: 'is-pass' },
  CALCULATION_STATUS_FAIL: { label: 'Fail', className: 'is-fail' },
  CALCULATION_STATUS_WARNING: { label: 'Warning', className: 'is-warning' },
  CALCULATION_STATUS_BLOCKED: { label: 'Blocked', className: 'is-blocked' },
  CALCULATION_STATUS_NOT_CALCULATED: { label: 'Not calculated', className: 'is-blocked' },
};

export function statusLabel(status: CalculationStatus | undefined): string {
  return STATUS_PRESENTATION[status ?? 'CALCULATION_STATUS_UNSPECIFIED'].label;
}

export function StatusBadge({ status }: { status: CalculationStatus | undefined }) {
  const presentation = STATUS_PRESENTATION[status ?? 'CALCULATION_STATUS_UNSPECIFIED'];
  return <span className={`status-badge ${presentation.className}`}>{presentation.label}</span>;
}

export function UtilizationBar({ fraction, status }: {
  fraction: number | undefined;
  status: CalculationStatus | undefined;
}) {
  if (fraction === undefined) return <div className="utilization-bar is-unknown" aria-hidden="true" />;
  const width = Math.max(0, Math.min(1, fraction)) * 100;
  const presentation = STATUS_PRESENTATION[status ?? 'CALCULATION_STATUS_UNSPECIFIED'];
  return (
    <div className="utilization-bar">
      <div className={`utilization-fill ${presentation.className}`} style={{ width: `${width}%` }} />
    </div>
  );
}

// ── Messages ─────────────────────────────────────────────────────────────────

const SEVERITY_CLASS: Record<Severity, string> = {
  SEVERITY_UNSPECIFIED: 'message',
  SEVERITY_INFO: 'message',
  SEVERITY_WARNING: 'message message-warning',
  SEVERITY_ERROR: 'message message-error',
};

export function MessageList({ messages, emptyText }: {
  messages: readonly ValidationMessage[];
  emptyText: string;
}) {
  if (messages.length === 0) return <p className="empty-hint">{emptyText}</p>;
  return (
    <ul className="message-list">
      {messages.map((message, index) => (
        <li
          key={`${message.code ?? ''}:${message.affected_object_id ?? ''}:${index}`}
          className={SEVERITY_CLASS[message.severity ?? 'SEVERITY_INFO']}
        >
          <div className="message-title">{message.title}</div>
          {message.explanation && <p className="message-body">{message.explanation}</p>}
          {message.required_action && <p className="message-action"><strong>Required action.</strong> {message.required_action}</p>}
          {message.code && <code className="message-code">{message.code}</code>}
        </li>
      ))}
    </ul>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="empty-hint">{children}</p>;
}
